import React, { useEffect, useState, useCallback, useRef } from "react";
import UserLayout from "../components/UserLayout";
import HelpAnchor from "../components/HelpAnchor";
import { useChatContext } from "../context/ChatContext";
import { useEinstufungProgress } from "../context/EinstufungProgressContext";
import { Toaster, toast } from "sonner";
import { Button } from "../components/ui/Button";
import { Label } from "../components/ui/Input";
import { api, extractError } from "../lib/api";
import StepThree from "./tool/StepThree";
import StepFour from "./tool/StepFour";
import ClarifyingQuestionsSection from "./tool/ClarifyingQuestionsSection";
import HaftungsausschlussModal from "../components/HaftungsausschlussModal";
import { mergeAusbildungsAutoMatches as mergeAuto, computeEffectiveTotal, mapBackendItem } from "./tool/shared";
import AiBusyOverlay from "../components/AiBusyOverlay";
import { isDemoActive, getActiveDemoCaseId } from "../demo/demoInterceptor";
import { getDemoCase } from "../demo/cases";
import {
  ArrowRight,
  ArrowLeft,
  ArrowsClockwise,
  BookOpenText,
  CheckCircle,
  Info,
  Warning,
  UploadSimple,
  FilePdf,
  FileImage,
  X,
  Check,
  Briefcase,
  Sparkle,
  Question,
  CaretRight,
  MagnifyingGlass,
  Star,
} from "@phosphor-icons/react";

const STEPS = [
  { n: "01", label: "Kollektivvertrag" },
  { n: "02", label: "Tätigkeit" },
  { n: "03", label: "Vordienstzeiten" },
  { n: "04", label: "Empfehlung" },
];

const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png", ".docx"];
const ALLOWED_MIME = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_FILE_BYTES = 15 * 1024 * 1024;
const MAX_FILES = 10;
const MAX_TEXT = 8000;
const CONFIDENCE_HIGH = 0.85;

/**
 * Iter 20.33 — Normalize a raw group label ("Verwendungsgruppe III" → "III")
 * consistent with backend `_normalize_gruppe`. Empty on garbage-in.
 */
function normalizeGroupLabel(raw) {
  return String(raw || "")
    .replace(/^(?:Verwendungs|Beschäftigungs|Meister)?gruppe\s+/i, "")
    .trim();
}

/**
 * Iter 20.33 — Heuristic sort rank for a group label used ONLY as fallback
 * when the KV does not have a curated `gruppen_order` string. Handles:
 *   • Roman numerals I…VIII → 1…8
 *   • Arabic digits ("1", "4", "12")
 *   • Alphabetic single letters ("A", "B", "H") → charCode
 *   • Compound labels "M I", "M II (mit Fachschule)" → [prefix, roman]
 *   • Anything else → lex-sorted after the above buckets
 * Returns a tuple [bucket, primary, secondary, raw] for stable Array.sort.
 */
const _ROMAN_MAP = {
  I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6, VII: 7, VIII: 8, IX: 9, X: 10,
  XI: 11, XII: 12,
};
function _heuristicRank(label) {
  const g = normalizeGroupLabel(label);
  const noParens = g.replace(/\s*\(.*?\)\s*$/, "").trim();
  if (!noParens) return [9, 0, 0, g];
  // Try compound: "M I", "M II"
  const compound = noParens.match(/^([A-Za-zÄÖÜäöü]+)\s+([IVX]+)$/i);
  if (compound) {
    const prefix = compound[1].toUpperCase();
    const roman = _ROMAN_MAP[compound[2].toUpperCase()];
    if (roman !== undefined) return [1, prefix.charCodeAt(0), roman, g];
  }
  // Pure roman
  if (/^[IVX]+$/i.test(noParens)) {
    const r = _ROMAN_MAP[noParens.toUpperCase()];
    if (r !== undefined) return [2, r, 0, g];
  }
  // Pure arabic (with optional trailing letter, e.g., "1a")
  const arabic = noParens.match(/^(\d+)([A-Za-z]?)$/);
  if (arabic) return [3, parseInt(arabic[1], 10), arabic[2].toUpperCase().charCodeAt(0) || 0, g];
  // Single/double letter
  if (/^[A-Za-zÄÖÜäöü]{1,3}$/.test(noParens)) {
    return [4, noParens.toUpperCase().charCodeAt(0), noParens.length > 1 ? noParens.toUpperCase().charCodeAt(1) : 0, g];
  }
  // Fallback: lex-sort
  return [5, 0, 0, noParens.toUpperCase()];
}

/**
 * Iter 20.33 — Sort AI-returned group options according to the KV's native
 * hierarchy (ascending). Priority:
 *   1. If `gruppenOrderString` is non-empty, respect that order strictly.
 *      Labels not listed in `gruppenOrderString` land at the end, sorted
 *      by the heuristic rank.
 *   2. Otherwise, sort purely by heuristic rank (Roman → Arabic → Alpha).
 */
function sortGroupOptions(options, gruppenOrderString) {
  const raw = Array.isArray(options) ? [...options] : [];
  const orderList = String(gruppenOrderString || "")
    .split(/[,\n;]/)
    .map((s) => normalizeGroupLabel(s))
    .filter(Boolean);
  const orderIndex = new Map(orderList.map((g, i) => [g, i]));
  return raw.sort((a, b) => {
    const ga = normalizeGroupLabel(a?.gruppe);
    const gb = normalizeGroupLabel(b?.gruppe);
    const ia = orderIndex.has(ga) ? orderIndex.get(ga) : Infinity;
    const ib = orderIndex.has(gb) ? orderIndex.get(gb) : Infinity;
    if (ia !== ib) return ia - ib;
    // Both unlisted OR both listed at same idx (unreachable) → heuristic tiebreak.
    const ra = _heuristicRank(ga);
    const rb = _heuristicRank(gb);
    for (let i = 0; i < ra.length; i++) {
      if (ra[i] < rb[i]) return -1;
      if (ra[i] > rb[i]) return 1;
    }
    return 0;
  });
}

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function StepIndicator({ currentStep }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-3 mb-8" data-testid="tool-step-indicator">
      {STEPS.map((step, i) => {
        const isCurrent = i === currentStep;
        const isDone = i < currentStep;
        return (
          <React.Fragment key={step.n}>
            <li className={`flex items-center gap-2.5 ${isCurrent ? "text-ink-900" : isDone ? "text-warm-700" : "text-warm-400"}`}>
              <span className={`flex items-center justify-center h-7 w-7 rounded-sm font-cabinet text-xs font-bold ${isCurrent ? "bg-brand-400 text-ink-900" : isDone ? "bg-ink-900 text-white" : "bg-ink-100 text-warm-500"}`}>
                {step.n}
              </span>
              <span className={`text-xs uppercase tracking-widest font-semibold ${isCurrent ? "text-ink-900" : isDone ? "text-warm-700" : "text-warm-400"}`}>
                {step.label}
              </span>
            </li>
            {i < STEPS.length - 1 && (<span className="h-px w-6 sm:w-10 bg-ink-200" aria-hidden="true" />)}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

function FileIcon({ kind }) {
  if (kind === "pdf") return <FilePdf size={18} weight="duotone" className="text-status-abgelehnt-text" />;
  return <FileImage size={18} weight="duotone" className="text-brand-500" />;
}

function fileKind(f) {
  const n = (f.name || "").toLowerCase();
  if (n.endsWith(".pdf")) return "pdf";
  if (n.endsWith(".png")) return "png";
  if (n.endsWith(".docx")) return "docx";
  return "jpg";
}

/**
 * Iter 19.46 — Footer-Block: Datenschutz + Haftungsausschluss (Kurzfassung).
 * Das letzte Wort "Haftungsausschluss" öffnet das gleiche Modal wie der
 * Consent-Link in Step 1.
 *
 * Iter 20.03 Runde 17 (Punkt B) — Konsolidierung war geplant, aber User-
 * Feedback (2026-02-21): „Bitte den Footer mit Datenschutz und
 * Haftungsausschluss auch in Schritt 2 beibehalten." → Ausführliche
 * dashed-Boxes bleiben unverändert.
 */
function DisclaimerFooter() {
  const [haftungOpen, setHaftungOpen] = React.useState(false);
  return (
    <div className="mt-8 space-y-3">
      <div
        className="flex items-start gap-3 border border-dashed border-warm-300 p-4 rounded-sm"
        data-testid="tool-footer-datenschutz"
      >
        <Info size={14} weight="bold" className="text-warm-500 mt-0.5 shrink-0" />
        <p className="text-[11px] text-warm-600 leading-relaxed">
          <strong className="text-ink-900">Datenschutz:</strong>{" "}
          ET speichert keine Mitarbeiter- oder Kundendaten.
          Sämtliche Eingaben innerhalb dieses Tools werden ausschließlich für den aktuellen Arbeitsschritt verarbeitet und anschließend verworfen.
        </p>
      </div>
      <div
        className="flex items-start gap-3 border border-dashed border-warm-300 p-4 rounded-sm"
        data-testid="tool-footer-haftung"
      >
        <Info size={14} weight="bold" className="text-warm-500 mt-0.5 shrink-0" />
        <p className="text-[11px] text-warm-600 leading-relaxed">
          <strong className="text-ink-900">Haftungsausschluss:</strong>{" "}
          Die Ergebnisse des Einstufungstools (ET) dienen ausschließlich als unverbindliche Unterstützung auf Basis der eingegebenen Daten.
          Trotz sorgfältiger Erstellung wird keine Gewähr für die Richtigkeit, Vollständigkeit oder rechtliche Verbindlichkeit der Ergebnisse übernommen.
          Das Tool ersetzt keine Rechts- oder Fachberatung. Die endgültige Verantwortung für die korrekte Einstufung, Entlohnung und rechtliche Beurteilung liegt
          ausschließlich bei den Nutzerinnen und Nutzern. Eine Haftung der Betreiberin für Schäden oder Nachteile aus der Nutzung des Tools ist ausgeschlossen.
          {" ("}
          <button
            type="button"
            onClick={() => setHaftungOpen(true)}
            className="text-brand-600 underline underline-offset-2 font-medium hover:text-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-1 rounded-sm"
            data-testid="tool-footer-haftung-link"
          >
            Haftungsausschluss
          </button>
          {")"}
        </p>
      </div>
      <HaftungsausschlussModal open={haftungOpen} onClose={() => setHaftungOpen(false)} />
    </div>
  );
}

function StepOne({
  kvs,
  loading,
  error,
  onRetry,
  selectedKvId,
  setSelectedKvId,
  onToggleFavorite,
  stichtag,
  setStichtag,
  consent,
  setConsent,
  onNext,
}) {
  const selectedKv = kvs.find((k) => k.id === selectedKvId) || null;
  const [haftungOpen, setHaftungOpen] = React.useState(false);
  // Iter 19.49 — Live-Filter für KV-Auswahl (skaliert bis 50+ KVs).
  const [kvFilter, setKvFilter] = React.useState("");
  // Iter 19.65.9 — Sortier-Logik: Favoriten zuerst (alphabetisch), dann
  // Rest. Wenn ein Filter aktiv ist, gelten die gleichen Regeln im Filter-
  // Subset. Wir speichern die Index-Position des ersten Nicht-Favoriten
  // für die optische Trennlinie.
  const { filteredKvs, firstNonFavIndex } = React.useMemo(() => {
    const q = kvFilter.trim().toLowerCase();
    const matches = q
      ? kvs.filter((kv) => {
          const name = (kv.name || "").toLowerCase();
          const kuerzel = (kv.kuerzel || "").toLowerCase();
          return name.includes(q) || kuerzel.includes(q);
        })
      : kvs;
    const sorted = [...matches].sort((a, b) => {
      const fa = a.is_favorite ? 0 : 1;
      const fb = b.is_favorite ? 0 : 1;
      if (fa !== fb) return fa - fb;
      return (a.name || "").localeCompare(b.name || "", "de");
    });
    const idx = sorted.findIndex((k) => !k.is_favorite);
    return { filteredKvs: sorted, firstNonFavIndex: idx };
  }, [kvs, kvFilter]);
  const hasFavorites = firstNonFavIndex !== 0 && filteredKvs.some((k) => k.is_favorite);
  const todayIso = React.useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  }, []);
  return (
    <section className="border border-ink-200 bg-white rounded-sm relative" data-testid="tool-step-1">
      <div className="absolute top-0 left-0 h-1 w-24 bg-brand-400" aria-hidden="true" />
      <div className="px-8 py-8 border-b border-ink-200">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm border border-ink-200 bg-ink-50">
            <BookOpenText size={22} weight="duotone" className="text-brand-500" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">Schritt 1 von 4</div>
            <h2 className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium leading-tight">Kollektivvertrag &amp; Stichtag wählen</h2>
            <p className="mt-2 text-sm text-warm-700 leading-relaxed">Kollektivvertrag festlegen und Stichtag für die Einstufung wählen.</p>
          </div>
          <HelpAnchor
            anchor="schritt-1"
            label="Hilfe zu Schritt 1 — KV &amp; Stichtag"
            testId="tool-step-1-help"
            className="shrink-0 mt-1"
          />
        </div>
      </div>
      <div className="px-8 py-7">
        {loading && (
          <div data-testid="tool-kv-loading">
            <Label>Kollektivvertrag wählen</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-24 rounded-sm border border-ink-200 bg-ink-50 loading-bar" />
              ))}
            </div>
            <div className="mt-2 text-xs text-warm-500">Wird geladen…</div>
          </div>
        )}
        {!loading && error && (
          <div role="alert" data-testid="tool-kv-error" className="flex items-start gap-3 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-4 py-3 rounded-sm text-sm text-status-abgelehnt-text">
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <div>{error}</div>
              <button onClick={onRetry} data-testid="tool-kv-retry" className="mt-2 inline-flex items-center gap-1 text-xs font-medium underline underline-offset-4 hover:no-underline">
                <ArrowsClockwise size={12} weight="bold" />Erneut laden
              </button>
            </div>
          </div>
        )}
        {!loading && !error && kvs.length === 0 && (
          <div data-testid="tool-kv-empty" className="border border-dashed border-warm-300 bg-warm-50 rounded-sm px-6 py-10 text-center">
            <BookOpenText size={28} weight="duotone" className="text-warm-500 mx-auto mb-3" />
            <div className="font-medium text-ink-900 mb-1">Noch keine Kollektivverträge verfügbar</div>
            <div className="text-xs text-warm-600 leading-relaxed max-w-md mx-auto">Die Administration hat aktuell keine Kollektivverträge hinterlegt.</div>
          </div>
        )}
        {!loading && !error && kvs.length > 0 && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
              <Label className="mb-0">Kollektivvertrag wählen</Label>
              {kvs.length > 6 && (
                <div className="relative w-full sm:w-72">
                  <MagnifyingGlass
                    size={14}
                    weight="bold"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-500 pointer-events-none"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    value={kvFilter}
                    onChange={(e) => setKvFilter(e.target.value)}
                    placeholder="KV durchsuchen…"
                    data-testid="tool-kv-search"
                    className="w-full rounded-sm border border-ink-300 bg-white pl-8 pr-8 py-2.5 text-sm text-ink-900 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                  />
                  {kvFilter && (
                    <button
                      type="button"
                      onClick={() => setKvFilter("")}
                      aria-label="Suche zurücksetzen"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-warm-500 hover:text-ink-900"
                      data-testid="tool-kv-search-clear"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  )}
                </div>
              )}
            </div>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5"
              role="radiogroup"
              aria-label="Kollektivvertrag"
              data-testid="tool-kv-grid"
            >
              {filteredKvs.map((kv, idx) => {
                const active = kv.id === selectedKvId;
                const showFavHeader = hasFavorites && idx === 0;
                const showFavSeparator = hasFavorites && idx === firstNonFavIndex;
                // Use admin-provided Kürzel; fall back to a 2-letter monogram
                // derived from the KV name when no Kürzel is set.
                const badgeLabel = (kv.kuerzel && kv.kuerzel.trim().toUpperCase()) || (() => {
                  const words = (kv.name || "")
                    .replace(/[()]/g, "")
                    .split(/\s+/)
                    .filter(Boolean);
                  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
                  return (words[0] || "KV").slice(0, 2).toUpperCase();
                })();
                return (
                  <React.Fragment key={kv.id}>
                    {showFavHeader && (
                      <div
                        className="col-span-full flex items-center gap-3 mb-1"
                        role="separator"
                        aria-label="Ihre Favoriten"
                        data-testid="tool-kv-fav-header"
                      >
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 whitespace-nowrap">
                          Ihre Favoriten
                        </div>
                        <div className="h-px flex-1 bg-ink-200" />
                      </div>
                    )}
                    {showFavSeparator && (
                      <div
                        className="col-span-full flex items-center gap-3 mt-2 mb-1"
                        role="separator"
                        aria-label="Alle Kollektivverträge"
                        data-testid="tool-kv-fav-separator"
                      >
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 whitespace-nowrap">
                          Alle Kollektivverträge
                        </div>
                        <div className="h-px flex-1 bg-ink-200" />
                      </div>
                    )}
                    <button
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setSelectedKvId(kv.id)}
                      data-testid={`tool-kv-card-${kv.id}`}
                      title={kv.name}
                      className={[
                        "group relative text-left rounded-sm p-3 transition-crisp focus:outline-none focus-visible:border-brand-400 focus-visible:ring-1 focus-visible:ring-brand-400 min-h-[96px] flex flex-col gap-2 overflow-hidden",
                        // Iter 19.65.11 — Border-Width für aktiv/inaktiv gleich
                        // halten (border-2), damit der Inhalt (Stern, Badge)
                        // beim Aktivieren nicht um 1px verrutscht.
                        // Iter 19.65.18 — overflow-hidden + shrinkbares Badge
                        // verhindert, dass lange Kürzel (z. B. wenn Admin den
                        // Volltext-KV-Namen ins Kürzel-Feld eingetragen hat)
                        // den Stern/Häkchen rechts aus der Card schieben.
                        active
                          ? "border-2 border-brand-400 bg-brand-50 shadow-[0_4px_24px_-12px_rgba(245,166,35,0.55)]"
                          : "border-2 border-ink-200 bg-white hover:border-brand-400 hover:shadow-[0_4px_24px_-12px_rgba(245,166,35,0.45)]",
                      ].join(" ")}
                    >
                      {/* Top row: Kürzel badge left, "Ausgewählt"-tick + Star top-right */}
                      <div className="flex items-start justify-between gap-2 min-w-0">
                        <div
                          className={[
                            "inline-flex items-center justify-center rounded-sm font-cabinet text-xs font-black tracking-wide transition-crisp px-2 min-w-[2.5rem] h-8 uppercase max-w-full",
                            active
                              ? "border border-brand-500 bg-brand-100 text-ink-900"
                              : "border border-brand-300 bg-brand-50 text-ink-900 group-hover:border-brand-400 group-hover:bg-brand-100",
                          ].join(" ")}
                          aria-hidden="true"
                        >
                          <span className="truncate">{badgeLabel}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {active && (
                            <CheckCircle
                              size={16}
                              weight="fill"
                              className="text-brand-600 shrink-0"
                              aria-label="Ausgewählt"
                            />
                          )}
                          {/* Iter 19.65.9 — Star-Favoriten-Toggle. Eigenes
                              span+role=button mit stopPropagation, damit der
                              Klick NICHT die KV-Auswahl der Card auslöst. */}
                          <span
                            role="button"
                            tabIndex={0}
                            aria-pressed={!!kv.is_favorite}
                            aria-label={kv.is_favorite ? `${kv.name} aus Favoriten entfernen` : `${kv.name} zu Favoriten hinzufügen`}
                            data-testid={`tool-kv-card-fav-${kv.id}`}
                            onClick={(e) => { e.stopPropagation(); onToggleFavorite && onToggleFavorite(kv.id); }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                onToggleFavorite && onToggleFavorite(kv.id);
                              }
                            }}
                            className={[
                              "inline-flex items-center justify-center w-6 h-6 rounded-sm transition-crisp",
                              "focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400",
                              "cursor-pointer hover:bg-warm-100",
                            ].join(" ")}
                          >
                            <Star
                              size={16}
                              weight={kv.is_favorite ? "fill" : "regular"}
                              className={
                                kv.is_favorite
                                  ? "text-brand-500"
                                  : "text-warm-400 group-hover:text-warm-600"
                              }
                            />
                          </span>
                        </div>
                      </div>

                      {/* KV name — 2-line clamp keeps tile heights uniform */}
                      <div
                        className="font-cabinet text-[13px] font-medium text-ink-900 leading-snug line-clamp-2"
                      >
                        {kv.name}
                      </div>

                      {/* Optional badges row — Iter 19.71: „Gleichwertigkeit"-
                          Badge entfernt (rein optisch; die Prüf-Logik in
                          Schritt 3 bleibt unverändert). */}
                      {kv.is_beta_for_me && (
                        <div className="mt-auto flex flex-wrap items-center gap-1.5">
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-semibold uppercase tracking-wider bg-ink-900 text-brand-300"
                            title="Sie sind Beta-Tester für diesen KV — er ist noch nicht für alle Anwender sichtbar."
                            data-testid={`tool-kv-card-beta-${kv.id}`}
                          >
                            Beta
                          </span>
                        </div>
                      )}
                  </button>
                  </React.Fragment>
                );
              })}
              {filteredKvs.length === 0 && (
                <div
                  data-testid="tool-kv-no-match"
                  className="col-span-full border border-dashed border-warm-300 bg-warm-50 rounded-sm px-4 py-8 text-center text-sm text-warm-700"
                >
                  Keine Kollektivverträge gefunden für „{kvFilter}{'"'}.
                </div>
              )}
            </div>
            <div className="mt-2 text-[11px] text-warm-500">
              {kvFilter && filteredKvs.length !== kvs.length
                ? `${filteredKvs.length} von ${kvs.length} ${kvs.length === 1 ? "Kollektivvertrag" : "Kollektivverträgen"} angezeigt.`
                : `${kvs.length} ${kvs.length === 1 ? "Kollektivvertrag" : "Kollektivverträge"} verfügbar.`}
            </div>

            {/* Stichtag */}
            <div className="mt-7" data-testid="tool-stichtag-wrap">
              <Label htmlFor="tool-stichtag">Stichtag der Einstufung</Label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <input
                  id="tool-stichtag"
                  type="date"
                  value={stichtag || todayIso}
                  onChange={(e) => setStichtag(e.target.value)}
                  data-testid="tool-stichtag-input"
                  className="rounded-sm border border-ink-300 bg-white px-3 py-2.5 text-sm text-ink-900 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 w-full sm:w-56"
                />
                {stichtag && stichtag !== todayIso && (
                  <button
                    type="button"
                    onClick={() => setStichtag(todayIso)}
                    data-testid="tool-stichtag-reset"
                    className="text-[11px] text-warm-500 hover:text-ink-900 transition-crisp inline-flex items-center gap-1 self-start sm:self-center"
                  >
                    <ArrowsClockwise size={11} weight="bold" />
                    Auf heute zurücksetzen
                  </button>
                )}
              </div>
              {/* Iter 20.05 — Softer Info-Box (Stichtag-Definition):
                  „laut Alert"-Look (brand-Border + brand-Hintergrund) auf
                  neutrales warm-Styling reduziert. Es ist eine Definition,
                  keine Warnung. */}
              <div
                className="mt-2 border-l border-warm-300 bg-warm-50/70 px-3 py-2 text-[11px] text-warm-700 leading-relaxed rounded-sm"
                data-testid="tool-stichtag-definition"
              >
                <strong className="text-ink-900">Stichtag</strong> = erster
                Arbeitstag im neuen Dienstverhältnis. Laufende Vorbeschäftigungen
                werden automatisch bis <strong>1 Tag vor dem Stichtag</strong>{" "}
                angerechnet (= letzter Tag vor Antritt).
              </div>
            </div>

            {/* Consent — for the whole flow (replaces former per-step checks) */}
            <label
              htmlFor="tool-step1-consent"
              className={[
                "mt-7 flex items-start gap-3 border rounded-sm px-4 py-3 cursor-pointer transition-crisp",
                consent
                  ? "border-status-aktiv-border bg-status-aktiv-bg"
                  : "border-ink-300 bg-white hover:border-ink-400",
              ].join(" ")}
              data-testid="tool-step1-consent-label"
            >
              <input
                id="tool-step1-consent"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-brand-500"
                data-testid="tool-step1-consent-checkbox"
              />
              <div className="text-sm text-ink-900 leading-relaxed">
                Ich akzeptiere den{" "}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setHaftungOpen(true);
                  }}
                  className="text-brand-600 underline underline-offset-2 font-medium hover:text-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-1 rounded-sm"
                  data-testid="tool-step1-haftung-link"
                >
                  Haftungsausschluss
                </button>{" "}
                und willige ein, dass die Daten zur Auswertung an ET
                übermittelt werden und diese vom System überprüft werden.
                Die übermittelten Dokumente werden nur für diesen Vorgang
                verwendet und nicht dauerhaft gespeichert.
              </div>
            </label>
            <HaftungsausschlussModal
              open={haftungOpen}
              onClose={() => setHaftungOpen(false)}
            />
          </>
        )}
      </div>
      <div className="px-8 py-5 border-t border-ink-200 flex justify-end bg-ink-50">
        <Button
          variant="brand"
          size="lg"
          disabled={!selectedKv || !consent}
          onClick={onNext}
          data-testid="tool-step1-next"
        >
          Weiter zur Tätigkeit
          <ArrowRight size={14} weight="bold" />
        </Button>
      </div>
    </section>
  );
}

function StepTwoInputs({ selectedKv, files, setFiles, description, setDescription, consent, setConsent, error, setError, submitting, onSubmit, onBack }) {
  const inputRef = useRef(null);
  // Text field is open by default — many users will only describe in text
  // without uploading any document. We don't hide it behind a toggle anymore.

  const addFiles = (incoming) => {
    setError("");
    const next = [...files];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) { setError(`Maximal ${MAX_FILES} Dateien pro Anfrage.`); break; }
      const lower = (f.name || "").toLowerCase();
      const okExt = ALLOWED_EXT.some((e) => lower.endsWith(e));
      const okMime = ALLOWED_MIME.includes(f.type);
      if (!okExt && !okMime) { setError(`„${f.name}" hat ein nicht erlaubtes Format. Zulässig sind PDF, DOCX, JPG und PNG.`); continue; }
      if (f.size > MAX_FILE_BYTES) { setError(`„${f.name}" überschreitet 15 MB.`); continue; }
      if (next.find((x) => x.name === f.name && x.size === f.size)) { continue; }
      next.push(f);
    }
    setFiles(next);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onFilePick = (e) => addFiles(Array.from(e.target.files || []));
  const onDrop = (e) => { e.preventDefault(); addFiles(Array.from(e.dataTransfer.files || [])); };
  const onDragOver = (e) => e.preventDefault();
  const removeFile = (idx) => { setFiles(files.filter((_, i) => i !== idx)); setError(""); };

  const totalSize = files.reduce((s, f) => s + f.size, 0);
  const canSubmit = consent && !submitting && (description.trim().length > 0 || files.length > 0);

  return (
    <section className="border border-ink-200 bg-white rounded-sm relative" data-testid="tool-step-2">
      <div className="absolute top-0 left-0 h-1 w-24 bg-brand-400" aria-hidden="true" />

      <div className="px-8 py-8 border-b border-ink-200">
        <div className="flex items-start gap-4">
          {/* Iter 20.03 Runde 17 (Punkt I) — Icon neutraler Farbton
              (text-ink-500) analog GraduationCap-Fix im Ausbildungsblock. */}
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm border border-ink-200 bg-ink-50">
            <Briefcase size={22} weight="duotone" className="text-ink-500" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">Schritt 2 von 4</div>
            <h2 className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium leading-tight">Tätigkeit erfassen</h2>
            {/* Iter 20.03 Runde 17 (Punkt E) — Kernaussage in 1 Satz.
                Der ehemalige Negativ-Hinweis („Lebenslauf … erst in Schritt 3")
                ist entfallen, weil Schritt 3 seine eigene UI dafür bereits klar
                kommuniziert. */}
            <p className="mt-2 text-sm text-warm-700 leading-relaxed">
              Laden Sie eine <strong>Stellenbeschreibung</strong> hoch, tippen Sie
              die Tätigkeit als Text ein — oder beides. ET schlägt daraus eine
              Beschäftigungsgruppe vor.
            </p>
          </div>
          <HelpAnchor
            anchor="schritt-2"
            label="Hilfe zu Schritt 2 — Dokumente hochladen"
            testId="tool-step-2-help"
            className="shrink-0 mt-1"
          />
        </div>
      </div>

      <div className="px-8 py-7 space-y-7">
        {/* PRIMARY: File upload */}
        <div data-testid="tool-upload-primary">
          <div className="flex items-end justify-between mb-2 gap-3 flex-wrap">
            <div>
              <Label className="mb-0">Stellenbeschreibung hochladen</Label>
              <div className="mt-1 text-[11px] text-warm-600">
                Stellenbeschreibung / Job Description als <strong>PDF, DOCX, JPG oder PNG</strong>
              </div>
            </div>
            {files.length > 0 && (
              <span className="text-[11px] text-warm-500">
                {files.length} / {MAX_FILES} Dokumente · {formatBytes(totalSize)} gesamt
              </span>
            )}
          </div>

          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            className="border-2 border-dashed border-ink-300 bg-warm-50 rounded-sm px-6 py-6 text-center transition-crisp hover:border-brand-400 hover:bg-brand-50"
            data-testid="tool-file-dropzone"
          >
            <UploadSimple size={28} weight="duotone" className="text-brand-500 mx-auto mb-2" />
            <div className="text-base text-ink-900 font-medium">
              Dateien hier ablegen oder{" "}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid="tool-file-pick"
                className="text-brand-700 underline underline-offset-2 hover:no-underline font-semibold"
              >
                auswählen
              </button>
            </div>
            {/* Iter 20.03 Runde 17 (Punkt H) — Sub-Info auf 2 Zeilen brechen,
                damit sie auch auf schmalen Screens sauber lesbar bleibt. */}
            <div className="text-xs text-warm-600 mt-2 leading-relaxed">
              <div>Mehrere Dateien gleichzeitig möglich · Maximum {MAX_FILES} Dokumente</div>
              <div>PDF, DOCX, JPG oder PNG · je bis 15 MB</div>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
              multiple
              onChange={onFilePick}
              className="sr-only"
              data-testid="tool-file-input"
            />
          </div>

          {files.length > 0 && (
            <ul className="mt-3 border border-ink-200 rounded-sm divide-y divide-ink-100 bg-white" data-testid="tool-file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-3 px-4 py-2.5" data-testid={`tool-file-${i}`}>
                  <FileIcon kind={fileKind(f)} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink-900 truncate">{f.name}</div>
                    <div className="text-[11px] text-warm-500">{formatBytes(f.size)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    aria-label={`„${f.name}" entfernen`}
                    data-testid={`tool-file-remove-${i}`}
                    className="text-warm-400 hover:text-status-abgelehnt-text transition-crisp"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </li>
              ))}
              {/* Iter 20.03 Runde 17 (Punkt K) — Erfolgs-Feedback nach Upload.
                  Kleiner grüner Bereit-Status als abschließender Row-Slot,
                  damit der User weiß: „Alles gut, Dokumente sind akzeptiert." */}
              <li
                className="flex items-center gap-2 px-4 py-2 bg-status-aktiv-bg/40 text-status-aktiv-text"
                data-testid="tool-file-ready-status"
              >
                <Check size={13} weight="bold" className="shrink-0" />
                <span className="text-[11px] font-medium">
                  {files.length === 1
                    ? "Bereit für die Auswertung."
                    : `${files.length} Dokumente bereit für die Auswertung.`}
                </span>
              </li>
            </ul>
          )}
        </div>

        {/* Iter 20.03 Runde 17 (Punkt D) — Menschlicherer Trenner statt
            technischem Boolean-Operator „UND / ODER". Klarer Hinweis, dass
            beide Wege gleichwertig sind und kombiniert werden können. */}
        <div
          className="relative flex items-center justify-center"
          data-testid="tool-step2-und-oder"
          aria-hidden="true"
        >
          <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-ink-200" />
          <span className="relative bg-white px-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-warm-500">
            oder auch als Text
          </span>
        </div>

        {/* SECONDARY: Text field — always visible (equal-weight alternative). */}
        <div data-testid="tool-text-secondary">
          <Label htmlFor="taetigkeit-text">
            Tätigkeit als Text beschreiben
            <span className="ml-2 text-[10px] tracking-normal text-warm-400 normal-case">
              {files.length > 0
                ? "(ergänzend zu den Dokumenten)"
                : "(direkt eintippen — kein Dokument nötig)"}
            </span>
          </Label>
          <textarea
            id="taetigkeit-text"
            data-testid="tool-taetigkeit-text"
            rows={5}
            maxLength={MAX_TEXT}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="z.B. „Buchhalterin im Einzelhandel, verantwortlich für Kreditorenbuchhaltung, Debitorenbuchhaltung und Lohnverrechnung. Selbständig nach Vorgaben der Geschäftsleitung. Führt ein kleines Team von 2 Personen."
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2.5 text-sm text-ink-900 placeholder:text-warm-400 transition-crisp focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 resize-y"
          />
          {/* Iter 20.03 Runde 17 (Punkt F) — Redundanter Sub-Text unter dem
              Textarea entfernt („Frei formulierter Text — beschreibt Tätigkeit
              …" doppelte sich zum Label darüber). Nur der Character-Counter
              bleibt rechts. */}
          <div className="mt-1 flex justify-end text-[10px] text-warm-400">
            <span>{description.length} / {MAX_TEXT}</span>
          </div>
        </div>

        {error && (
          <div role="alert" data-testid="tool-step2-error" className="flex items-start gap-2 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2.5 rounded-sm text-sm text-status-abgelehnt-text">
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="px-8 py-5 border-t border-ink-200 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 bg-ink-50">
        <Button variant="secondary" size="lg" onClick={onBack} data-testid="tool-step2-back">
          <ArrowLeft size={14} weight="bold" />
          Zurück
        </Button>
        {/* Iter 20.03 Runde 17 (Punkt G) — Submit-Hint war zunächst geplant,
            wurde aber nach User-Feedback (2026-02-21) wieder zurückgenommen —
            der disabled-Button erklärt sich selbst durch die zwei sichtbaren
            Eingabefelder darüber. */}
        <Button variant="brand" size="lg" disabled={!canSubmit} onClick={onSubmit} data-testid="tool-step2-submit">
          {submitting ? "Auswertung läuft…" : "Auswertung starten"}
          <ArrowRight size={14} weight="bold" />
        </Button>
      </div>
    </section>
  );
}

function StepTwoLoading() {
  return (
    <section className="border border-ink-200 bg-white rounded-sm relative min-h-[600px] flex flex-col" data-testid="tool-step-2-loading">
      <div className="absolute top-0 left-0 h-1 w-full bg-brand-400 animate-pulse" aria-hidden="true" />
      <div className="flex-1 px-8 py-16 text-center flex flex-col items-center justify-center">
        <div className="mx-auto mb-6 flex items-center justify-center">
          <span className="relative inline-flex h-16 w-16 items-center justify-center" aria-hidden="true">
            {/* Soft pulsating brand-orange halo */}
            <span className="absolute inset-0 rounded-full bg-brand-400/30 animate-ping" />
            <span
              className="absolute inset-1 rounded-full bg-brand-400/20"
              style={{
                animation: "et-loading-pulse 1.8s ease-in-out infinite",
              }}
            />
            <img
              src="/et-mark-dark.png"
              alt="ET"
              className="relative h-12 w-auto"
              style={{
                animation: "et-loading-bob 2.4s ease-in-out infinite",
              }}
              draggable={false}
            />
          </span>
        </div>
        <div className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium leading-tight">
          ET analysiert Ihre Eingaben…
        </div>
        <div className="mt-7 mx-auto w-full max-w-md h-0.5 loading-bar-brand" />
        <div className="mt-3 text-[11px] text-warm-500">
          Dies kann je nach Umfang 10-60 Sekunden dauern.
        </div>
      </div>
    </section>
  );
}

function ConfidenceMeter({ value }) {
  const pct = Math.round((value || 0) * 100);
  const isHigh = (value || 0) >= CONFIDENCE_HIGH;
  return (
    <div className="space-y-1.5" data-testid="tool-confidence-meter">
      <div className="flex justify-between text-[10px] uppercase tracking-widest font-semibold text-warm-500">
        <span>Sicherheitswert</span>
        <span className={isHigh ? "text-status-aktiv-text" : "text-brand-700"}>{pct}%</span>
      </div>
      <div className="h-1.5 w-full bg-ink-100 rounded-sm overflow-hidden">
        <div
          className={`h-full transition-all ${isHigh ? "bg-status-aktiv-border" : "bg-brand-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-[11px] text-warm-500">Schwelle für direkte Empfehlung: 85 %</div>
    </div>
  );
}

/**
 * Iter 20.32 — Rotating micro-steps for the re-analyze loading overlay.
 * Cycles through a fixed list of German status labels while `active === true`.
 * Resets to index 0 when it goes inactive.
 */
function useRotatingLabels(labels, active, intervalMs = 1800) {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    if (!active) {
      setI(0);
      return;
    }
    const id = setInterval(() => {
      setI((k) => (k + 1) % labels.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [active, labels.length, intervalMs]);
  return labels[i] || labels[0] || "";
}

const REANALYZE_MICRO_STEPS = [
  "Antworten werden gelesen",
  "Tätigkeit wird mit KV-Struktur abgeglichen",
  "Beschäftigungsgruppen werden verglichen",
];

/**
 * Iter 20.32 — Group Selection Cards with adaptive layout.
 *
 * Rendering rules:
 *   • ≤ 4 options → 2-column responsive grid (like before).
 *   • ≥ 5 options → horizontal scroll-snap carousel with subtle Prev/Next
 *     chevron buttons and a soft mask fade on both ends. Non-focused
 *     cards keep normal opacity — the highlight is done via ring styles.
 *
 * Visual states per card:
 *   • Recommended by AI: emerald `ring-2` + soft glow + "KI-Empfehlung" badge.
 *   • Selected (user click): brand-orange `ring-2` + Check-icon.
 *   • Recommended + Selected: emerald ring stays; small "Ausgewählt" pill added.
 *   • Neither: neutral border, hover elevates.
 */
function GroupOptionCard({ opt, index, isRecommended, isSelected, onSelect }) {
  const g = String(opt.gruppe || "").replace(
    /^(?:Verwendungs|Beschäftigungs|Meister)?gruppe\s+/i,
    "",
  ).trim() || "—";
  // Iter 20.36.f12 — Manuell hinzugefügte Karten (aus dem „Alle Gruppen
  // anzeigen"-Toggle) haben keinen `beschreibung`- / Confidence-Wert. Wir
  // rendern sie mit einem dezent-neutralen Look, damit sie klar von den
  // AI-Vorschlägen unterscheidbar sind.
  const isManual = Boolean(opt._manual);
  const ringClass = isRecommended
    ? "ring-2 ring-emerald-400 shadow-[0_8px_28px_-8px_rgba(52,211,153,0.55)] bg-white"
    : isSelected
      ? "ring-2 ring-brand-400 shadow-[0_8px_28px_-12px_rgba(245,166,35,0.55)] bg-white"
      : isManual
        ? "border border-dashed border-ink-300 bg-ink-50/40 hover:border-brand-400 hover:bg-white"
        : "border border-ink-200 bg-white hover:border-brand-400 hover:shadow-[0_4px_24px_-12px_rgba(245,166,35,0.35)]";
  return (
    <button
      type="button"
      onClick={() => onSelect(g)}
      data-testid={`tool-option-${index}`}
      data-selected={isSelected ? "true" : "false"}
      data-recommended={isRecommended ? "true" : "false"}
      className={`group relative w-full h-full text-left rounded-sm p-4 transition-crisp focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 ${ringClass}`}
    >
      {isRecommended && (
        <div
          className="absolute -top-2.5 left-3 inline-flex items-center gap-1 rounded-sm bg-emerald-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white shadow-sm"
          data-testid={`tool-option-${index}-recommended-badge`}
        >
          <Sparkle size={9} weight="fill" />
          ET-Vorschlag
        </div>
      )}
      {isSelected && !isRecommended && (
        <div className="absolute -top-2.5 right-3 inline-flex items-center gap-1 rounded-sm bg-brand-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white shadow-sm">
          <Check size={9} weight="bold" />
          Ausgewählt
        </div>
      )}
      {isSelected && isRecommended && (
        <div className="absolute -top-2.5 right-3 inline-flex items-center gap-1 rounded-sm bg-emerald-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white shadow-sm">
          <Check size={9} weight="bold" />
          Ausgewählt
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className={`flex h-10 min-w-10 shrink-0 items-center justify-center rounded-sm px-2 font-cabinet text-base font-black text-ink-900 border ${isRecommended ? "border-emerald-400 bg-emerald-50" : isSelected ? "border-brand-400 bg-brand-50" : isManual ? "border-ink-300 bg-white" : "border-brand-300 bg-brand-50 group-hover:border-brand-400 group-hover:bg-brand-100"}`}>
          {g}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 break-words">
            Beschäftigungsgruppe {g}
            {isManual && (
              <span
                className="ml-1.5 inline-flex items-center rounded-sm border border-ink-300 bg-white px-1.5 py-0.5 text-[9px] font-bold text-ink-600 tracking-widest normal-case"
                data-testid={`tool-option-${index}-manual-badge`}
              >
                MANUELL
              </span>
            )}
          </div>
          <div className="text-sm text-ink-900 leading-snug break-words hyphens-auto" lang="de">
            {opt.beschreibung || (isManual ? (
              <span className="italic text-warm-500">
                Nicht als Vorschlag von ET — nur wählen, wenn Sie die Gruppe bewusst überschreiben möchten.
              </span>
            ) : null)}
          </div>
        </div>
      </div>
    </button>
  );
}

function GroupOptions({ options, recommendedGroup, selectedGroup, onSelect, gruppenOrder }) {
  // Iter 20.33 — Native KV-Reihenfolge respektieren; Fallback: heuristische
  // Sortierung (Römisch → Arabisch → Alphabet).
  const sortedOptions = React.useMemo(
    () => sortGroupOptions(options, gruppenOrder),
    [options, gruppenOrder],
  );

  // Normalize the recommended-group key (strip any legacy prefix) so
  // comparisons are consistent with the value the option itself carries.
  const normalizedRecommended = normalizeGroupLabel(recommendedGroup);

  // Iter 20.36.f14.5 — Carousel entfernt. Der bisherige Horizontal-Scroll
  // mit Mask-Gradient wirkte gedrängt und schnitt Karten links/rechts an.
  // Stattdessen: responsives Grid, das alle Karten gleichzeitig zeigt.
  //
  // Iter 20.36.f14.13 — Kachel-Breite hochgezogen: max. 2 Spalten. Das
  // gibt jeder Karte auf Desktop ~600 px Breite, sodass die Beschreibungs-
  // texte flüssig auf 3-4 Zeilen laufen ohne Compound-Wort-Umbrüche. Das
  // „Alle Gruppen anzeigen"-Toggle fügt einfach mehr Karten in dasselbe
  // Raster (weitere Reihen, gleiche Karten-Breite).
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2"
      data-testid="tool-result-options"
    >
      {sortedOptions.map((opt, i) => {
        const g = normalizeGroupLabel(opt?.gruppe);
        return (
          <GroupOptionCard
            key={`${g}-${i}`}
            opt={opt}
            index={i}
            isRecommended={g === normalizedRecommended && Boolean(normalizedRecommended)}
            isSelected={g === selectedGroup}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
}

function ResultBlock({ result, onEdit, onContinue, onReanalyze, onCancelReanalyze, reanalyzing, gruppenOrder }) {
  const isHigh = (result.confidence_score || 0) >= CONFIDENCE_HIGH;
  const [answers, setAnswers] = useState({});
  const answeredCount = Object.values(answers).filter((a) => (a || "").trim()).length;

  // Iter 20.32 — Selectable groups. `selectedGroup` starts as the user's
  // previous choice (if the user is returning from Step 3) — otherwise it
  // stays EMPTY on first render. The AI-Vorschlag is only visually highlighted
  // (green glow); it is NOT auto-selected. This forces an explicit click so
  // the "Weiter zu Schritt 3"-Button starts disabled and reads
  // "Bitte oben eine Gruppe auswählen".
  //
  // `original_suggested_beschaeftigungsgruppe` is the ANCHOR for the emerald
  // ET-Vorschlag highlight — it never changes after the initial analyse call,
  // so returning from Step 3 still shows ET's original pick with the green
  // glow, while `user_selected_group` (set only on explicit Continue clicks)
  // drives the brand-orange "AUSGEWÄHLT"-Ring.
  const rawOptions = Array.isArray(result.gruppen_optionen)
    ? result.gruppen_optionen
    : [];
  const aiRecommendedRaw =
    result.original_suggested_beschaeftigungsgruppe ||
    result.suggested_beschaeftigungsgruppe ||
    "";
  const normalizedRecommended = String(aiRecommendedRaw).replace(
    /^(?:Verwendungs|Beschäftigungs|Meister)?gruppe\s+/i,
    "",
  ).trim();
  const userSelectedRaw = String(result.user_selected_group || "").replace(
    /^(?:Verwendungs|Beschäftigungs|Meister)?gruppe\s+/i,
    "",
  ).trim();
  const [selectedGroup, setSelectedGroup] = useState(userSelectedRaw);

  // Iter 20.36.f12 — „Alle Gruppen anzeigen"-Toggle. Zusätzliche Gruppen
  // aus `gruppenOrder` (die ALLE KV-Gruppen kommagetrennt enthält) werden
  // gemerged, falls sie nicht schon Teil der AI-Options sind. Auto-
  // aktiviert, wenn der User (Return aus Schritt 3) eine Gruppe ausgewählt
  // hatte, die NICHT in den AI-Vorschlägen war — sonst wäre die Karte
  // nicht sichtbar.
  const rawOptionKeys = React.useMemo(
    () => new Set(rawOptions.map((o) => normalizeGroupLabel(o?.gruppe))),
    [rawOptions],
  );
  const hiddenGroups = React.useMemo(() => {
    const orderList = String(gruppenOrder || "")
      .split(/[,\n;]/)
      .map((s) => normalizeGroupLabel(s))
      .filter(Boolean);
    return orderList.filter((g) => !rawOptionKeys.has(g));
  }, [gruppenOrder, rawOptionKeys]);
  const mergedOptions = React.useMemo(() => {
    if (hiddenGroups.length === 0) return rawOptions;
    return [
      ...rawOptions,
      ...hiddenGroups.map((g) => ({ gruppe: g, _manual: true })),
    ];
  }, [rawOptions, hiddenGroups]);
  const [showAllGroups, setShowAllGroups] = useState(
    () => Boolean(userSelectedRaw) && !rawOptionKeys.has(userSelectedRaw),
  );

  // Iter 20.32 — Rotating micro-steps in the re-analyze overlay (Option B).
  const rotatingLabel = useRotatingLabels(REANALYZE_MICRO_STEPS, reanalyzing);

  return (
    <section className="border border-ink-200 bg-white rounded-sm relative overflow-hidden" data-testid="tool-step2-result">
      <div className={`absolute top-0 left-0 h-1 w-32 ${isHigh ? "bg-status-aktiv-border" : "bg-brand-400"}`} aria-hidden="true" />
      <div className="px-8 py-8 border-b border-ink-200 flex items-start gap-4">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-sm border ${isHigh ? "border-status-aktiv-border bg-status-aktiv-bg" : "border-brand-300 bg-brand-50"}`}>
          {isHigh ? (
            <CheckCircle size={22} weight="duotone" className="text-status-aktiv-text" />
          ) : (
            <Question size={22} weight="duotone" className="text-brand-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
            {isHigh ? "ET-Vorschlag" : "Auswahl & Rückfragen"}
          </div>
          <h2 className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium leading-tight">
            {isHigh ? (
              <>
                Beschäftigungsgruppe{" "}
                <span className="text-brand-500" data-testid="tool-result-group">
                  {normalizedRecommended || "—"}
                </span>
              </>
            ) : (
              "Bitte wählen Sie die passende Beschäftigungsgruppe"
            )}
          </h2>
          <p className="mt-2 text-sm text-warm-700 leading-relaxed">
            {isHigh
              ? "ET schlägt die grün markierte Gruppe vor. Sie können jederzeit eine andere wählen und mit Schritt 3 fortfahren."
              : "Die Daten reichen für eine eindeutige Einstufung noch nicht aus. Wählen Sie aus den vorgeschlagenen Beschäftigungsgruppen die zutreffende aus — oder ergänzen Sie die offenen Rückfragen und lassen Sie erneut analysieren."}
          </p>
        </div>
      </div>

      <div className="px-8 py-7 space-y-6 relative">
        {reanalyzing && (
          <AiBusyOverlay
            label="ET liest Ihre Antworten …"
            hint={rotatingLabel}
            testId="tool-step2-busy-overlay"
            onCancel={onCancelReanalyze}
          />
        )}
        <ConfidenceMeter value={result.confidence_score} />

        {result.rationale && (
          <div data-testid="tool-result-rationale">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
              Begründung
            </div>
            <p className="text-sm text-ink-900 leading-relaxed">{result.rationale}</p>
          </div>
        )}

        {result.extracted_facts && (result.extracted_facts.taetigkeit_kurz || result.extracted_facts.qualifikation) && (
          <div data-testid="tool-result-facts" className="grid grid-cols-1 sm:grid-cols-2 gap-0 border border-ink-200 rounded-sm divide-y sm:divide-y-0 sm:divide-x divide-ink-200 bg-warm-50">
            {[
              { label: "Tätigkeit", value: result.extracted_facts.taetigkeit_kurz },
              { label: "Qualifikation", value: result.extracted_facts.qualifikation },
            ].map((row) => (
              <div key={row.label} className="p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">{row.label}</div>
                <div className="text-sm text-ink-900 leading-relaxed">{row.value || <span className="text-warm-400">—</span>}</div>
              </div>
            ))}
          </div>
        )}

        {rawOptions.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
              {isHigh ? "Beschäftigungsgruppe wählen" : "Mögliche Beschäftigungsgruppen"}
            </div>
            <div className="text-xs text-warm-600 leading-relaxed mb-2">
              {isHigh
                ? "Der ET-Vorschlag ist grün markiert. Klicken Sie eine Karte, um zu wählen."
                : "Klicken Sie die passende Gruppe an. Wenn keine passt, beantworten Sie die Rückfragen für eine erneute Auswertung."}
            </div>
            <GroupOptions
              options={showAllGroups ? mergedOptions : rawOptions}
              recommendedGroup={aiRecommendedRaw}
              selectedGroup={selectedGroup}
              onSelect={setSelectedGroup}
              gruppenOrder={gruppenOrder}
            />
            {/* Iter 20.36.f12 — Alle Gruppen anzeigen: Fallback für Fälle in
                denen der User selbst die Gruppe bestimmen will (z. B. wenn
                die AI-Vorschläge nur eine Teilmenge der KV-Gruppen umfassen,
                Beispiel KV Steuerberater: "b)"-Varianten fehlten). Der
                Toggle merged nicht-vorgeschlagene Gruppen aus `gruppenOrder`
                mit einer schlichten Karte (kein Confidence-Wert) und
                markiert sie visuell als „manuelle Auswahl". */}
            {hiddenGroups.length > 0 && (
              <div className="mt-3 flex justify-center" data-testid="tool-result-toggle-all-groups-wrap">
                <button
                  type="button"
                  onClick={() => setShowAllGroups((v) => !v)}
                  className="text-xs uppercase tracking-widest font-semibold text-warm-600 hover:text-ink-900 border border-ink-200 hover:border-ink-400 bg-white hover:bg-ink-50 rounded-sm px-3 py-1.5 transition-crisp"
                  data-testid="tool-result-toggle-all-groups"
                  aria-expanded={showAllGroups}
                >
                  {showAllGroups
                    ? `Weniger anzeigen (${rawOptions.length} vorgeschlagene)`
                    : `Alle Gruppen anzeigen (+ ${hiddenGroups.length} weitere)`}
                </button>
              </div>
            )}
          </div>
        )}

        {!isHigh && result.clarifying_questions?.length > 0 && (
          <ClarifyingQuestionsSection
            questions={result.clarifying_questions}
            answers={answers}
            setAnswers={setAnswers}
            reanalyzing={reanalyzing}
            onReanalyze={onReanalyze}
          />
        )}

      </div>

      <div className="px-8 py-5 border-t border-ink-200 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 bg-ink-50">
        <Button variant="secondary" size="lg" onClick={onEdit} data-testid="tool-result-edit">
          <ArrowLeft size={14} weight="bold" />
          Eingaben ergänzen
        </Button>
        <Button
          variant="brand"
          size="lg"
          disabled={!selectedGroup}
          onClick={() => onContinue(selectedGroup)}
          data-testid="tool-result-continue"
        >
          {selectedGroup
            ? `Weiter zu Schritt 3 mit Gruppe „${selectedGroup}"`
            : "Bitte oben eine Gruppe auswählen"}
          <ArrowRight size={14} weight="bold" />
        </Button>
      </div>
    </section>
  );
}

export default function EinstufungToolPage() {
  // Iter 20.09 — Demo-Modus-Detection. Bei aktivem `sessionStorage.et_demo_case`
  // wird die Description in Step 2 sowie die Vordienstzeiten/Ausbildungszeiten
  // in Step 3 direkt mit den Case-Beispieldaten initialisiert. So sieht der
  // Interessent bereits sinnvoll ausgefüllte Felder und kann sich auf die
  // Kern-Interaktionen (Klassifikation, KI-Empfehlung) konzentrieren.
  const demoCase = isDemoActive() ? getDemoCase(getActiveDemoCaseId()) : null;

  const [kvs, setKvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedKvId, setSelectedKvId] = useState("");
  const [step, setStep] = useState(0);
  // Step 2 state
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState(
    () => demoCase?.initial?.description || ""
  );
  const [consent, setConsent] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const analyzeAbortRef = useRef(null);

  const cancelAnalyze = () => {
    if (analyzeAbortRef.current) {
      analyzeAbortRef.current.abort();
      analyzeAbortRef.current = null;
    }
  };
  // Step 3 state — vordienstzeiten array (lifted to page so Step 4 can read it)
  const [vordienstzeiten, setVordienstzeiten] = useState(() => {
    if (!demoCase?.extractResponse?.result?.items) return [];
    return demoCase.extractResponse.result.items.map((it) => mapBackendItem(it));
  });
  // Step 3 state — ausbildungszeiten (fictive credits like HAK-Matura).
  // Populated by AI auto-match after step 2 OR manually by the user.
  const [ausbildungszeiten, setAusbildungszeiten] = useState(
    () => demoCase?.analyzeResponse?.auto_matched_ausbildung || []
  );
  // Iter 19.43.5 — Backend-autoritativer Aggregate-Wert. Wird nach jedem
  // Backend-Call (extract / review / final-recommendation) gesetzt und in
  // der Sidebar bevorzugt vor der Frontend-Approximation. Bei User-Edit
  // der Items wird er invalidiert (auf null), bis ein neuer Backend-Call
  // erfolgt. Shape: { display, jahre, monate, tage, totalDays } | null.
  //
  // Iter 20.09 — Demo-Modus: Aggregate direkt mit dem Case-Wert
  // vorbelegen, damit der „Empfehlung erstellen"-Button ohne wartenden
  // Recalc-Roundtrip aktiv ist. Der useDebouncedAutoRecalc überspringt
  // beim First-Trigger sowieso — ohne Vorbelegung würde der Button
  // permanent disabled bleiben.
  const [backendAggregate, setBackendAggregate] = useState(() => {
    if (!demoCase) return null;
    const monate = demoCase.extractResponse?.result?.anrechenbare_monate || 0;
    const jahre = Math.floor(monate / 12);
    const restMonate = Math.round(monate - jahre * 12);
    return {
      display: `${jahre} J. ${restMonate} M.`,
      jahre,
      monate: restMonate,
      tage: 0,
      totalDays: jahre * 360 + restMonate * 30,
    };
  });
  // Wrapper-Setter, der `backendAggregate` invalidiert wenn der User Items manuell editiert / hinzufügt / löscht. Backend-Aufrufer
  // (StepThree-Extract, StepFour-Recommendation) rufen `setVordienstzeiten`
  // direkt UND setzen `backendAggregate` neu — die Invalidierung greift dort
  // nicht doppelt, weil der State direkt überschrieben wird.
  const setVordienstzeitenWithInvalidate = useCallback((updater) => {
    setVordienstzeiten(updater);
    setBackendAggregate(null);
  }, []);
  const setAusbildungszeitenWithInvalidate = useCallback((updater) => {
    setAusbildungszeiten(updater);
    setBackendAggregate(null);
  }, []);

  // Stichtag (default = heute, format YYYY-MM-DD)
  const [stichtag, setStichtag] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  });

  // Iter 20.09 — Bei Step-Wechsel im Demo-Modus ein CustomEvent feuern,
  // damit der DemoCoach den passenden Hinweis anzeigt (0..3 → Schritt 1..4).
  useEffect(() => {
    if (isDemoActive()) {
      window.dispatchEvent(
        new CustomEvent("et:demo-step-change", { detail: step })
      );
    }
  }, [step]);

  // Broadcast the currently-selected KV to the floating ChatBubble so the
  // assistant can deep-context to the same KV the user is working with.
  const { setCurrentKv } = useChatContext();
  useEffect(() => {
    if (!selectedKvId) {
      setCurrentKv(null);
      return;
    }
    const kv = kvs.find((k) => k.id === selectedKvId);
    if (kv) setCurrentKv({ id: kv.id, name: kv.name });
  }, [selectedKvId, kvs, setCurrentKv]);

  // Sidebar Wizard-Live-View — push the running summary of inputs/results
  // once the user has confirmed each step (progressive disclosure).
  const { setProgress, resetProgress } = useEinstufungProgress();

  useEffect(() => {
    // Step 1 confirmed → Stichtag + KV visible in sidebar.
    if (step >= 1 && selectedKvId) {
      const kv = kvs.find((k) => k.id === selectedKvId);
      if (kv) {
        setProgress({
          stichtag,
          kvId: kv.id,
          kvName: kv.name,
          kvKuerzel: kv.kuerzel || null,
        });
      }
    }
  }, [step, selectedKvId, kvs, stichtag, setProgress]);

  useEffect(() => {
    // Step 2 confirmed → Tätigkeit / Qualifikation / KI-Vorschlag visible.
    if (step >= 2 && aiResult) {
      const facts = aiResult.extracted_facts || {};
      setProgress({
        taetigkeitKurz: facts.taetigkeit_kurz || description.trim().slice(0, 120) || null,
        qualifikation: facts.qualifikation || null,
        suggestedGroup: aiResult.suggested_beschaeftigungsgruppe || null,
      });
    }
  }, [step, aiResult, description, setProgress]);

  useEffect(() => {
    // Step 3 confirmed → Anrechenbare Monate live-updated.
    // Bypass-cap Ausbildungs-credits are added on top, non-bypass go into
    // the capped pool together with employment months.
    if (step >= 3) {
      // Iter 19.43.5 — Wenn Backend-autoritativer Wert vorhanden (nach
      // letztem extract/review/final-recommendation), nimm DEN. Sonst
      // zeige "Neuberechnung notwendig" (Iter 19.43.7) — die Frontend-
      // Approximation hat in Edge-Cases zu Verwirrung geführt; eine ehrliche
      // "stale"-Anzeige ist besser als ein irreführender Schätzwert.
      if (backendAggregate && backendAggregate.display) {
        // Iter 19.63.13 / 19.63.37 / 19.63.38 — Ausbildungs-Bonuszeiten für Sidebar-Card-03.
        //
        // Math zentralisiert in `computeEffectiveTotal()` (siehe shared.js).
        // Identisch zu StepThree.jsx finalAggregate und Backend
        // final_recommendation.py Z. 778-782.
        const _kv = kvs.find((x) => x.id === selectedKvId);
        const _capMonths = _kv && Number(_kv.cap_months) > 0 ? Number(_kv.cap_months) : 0;
        const _eff = computeEffectiveTotal({
          vordienstDays: backendAggregate.totalDays || 0,
          ausbildungszeiten,
          capMonths: _capMonths,
        });
        const hasAnyBonus = _eff.normalBonusMonths > 0 || _eff.bypassMonths > 0;
        const effectiveDisplay = hasAnyBonus ? _eff.display : backendAggregate.display;
        setProgress({
          anrechenbareMonate: _eff.effectiveTotalDays / 30,
          anrechenbareTage: _eff.effectiveTotalDays,
          anrechenbareDisplay: effectiveDisplay,
          anrechenbareStale: false,
        });
      } else if ((vordienstzeiten || []).length > 0) {
        // Stale-State: User hat manuell editiert seit dem letzten KI-Check.
        // Wir setzen einen Marker-Wert (1) damit die Sidebar-Card sichtbar
        // bleibt (mountet nur wenn anrechenbareMonate > 0).
        setProgress({
          anrechenbareMonate: 1,
          anrechenbareTage: 0,
          anrechenbareDisplay: "Neuberechnung nötig",
          anrechenbareStale: true,
        });
      }
    }
  }, [step, vordienstzeiten, ausbildungszeiten, stichtag, kvs, selectedKvId, backendAggregate, setProgress]);

  const loadKvs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/tool/kvs");
      setKvs(data.items || []);
    } catch (err) {
      setError(extractError(err, "Kollektivverträge konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKvs(); }, [loadKvs]);

  // Iter 19.65.9 — Favoriten-Toggle. Optimistic UI: zuerst lokal flippen,
  // dann Server-Call. Bei Fehler reverten + Toast.
  //
  // Iter 19.65.11 — Bugfix: `prev` darf NICHT innerhalb des setKvs-Updaters
  // gesetzt und außerhalb gelesen werden, da React 18 den Updater
  // asynchron/gebatched ausführt. Folge: `prev` war beim API-Call oft noch
  // `undefined`, wodurch fälschlich ein POST statt DELETE gefeuert wurde
  // — der Favorit blieb in der DB bestehen und kam beim Reload als ★
  // zurück. Wir lesen den Vor-Zustand jetzt synchron aus der aktuellen
  // `kvs`-Closure.
  const toggleFavorite = useCallback(async (kvId) => {
    const kvBefore = kvs.find((k) => k.id === kvId);
    if (!kvBefore) return;
    const wasFavorite = !!kvBefore.is_favorite;

    // Optimistic UI flip.
    setKvs((cur) =>
      cur.map((kv) => (kv.id === kvId ? { ...kv, is_favorite: !wasFavorite } : kv))
    );

    try {
      if (wasFavorite) {
        await api.delete(`/user/favorites/${kvId}`);
      } else {
        await api.post(`/user/favorites/${kvId}`);
      }
    } catch (err) {
      // Revert on failure.
      setKvs((cur) =>
        cur.map((kv) => (kv.id === kvId ? { ...kv, is_favorite: wasFavorite } : kv))
      );
      toast.error(extractError(err, "Favorit konnte nicht aktualisiert werden."));
    }
  }, [kvs]);

  const selectedKv = kvs.find((k) => k.id === selectedKvId) || null;

  const handleStep1Next = () => {
    if (!selectedKv) return;
    setStep(1);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const handleStep2Submit = async () => {
    if (!consent) { setSubmitErr("Bitte aktivieren Sie zuerst die Datenschutz-Einwilligung."); return; }
    if (!description.trim() && files.length === 0) {
      setSubmitErr("Bitte mindestens ein Dokument hochladen oder eine Tätigkeitsbeschreibung eingeben.");
      return;
    }
    setSubmitErr("");
    setSubmitting(true);
    setAiResult(null);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
    const controller = new AbortController();
    analyzeAbortRef.current = controller;
    try {
      const fd = new FormData();
      fd.append("kv_id", selectedKv.id);
      fd.append("description", description.trim());
      fd.append("consent_ack", "true");
      for (const f of files) fd.append("files", f, f.name);
      const { data } = await api.post("/tool/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
        signal: controller.signal,
      });
      const ai = data?.ai;
      if (!ai) throw new Error("Unerwartete Antwort vom System.");
      // Iter 20.32 — Preserve the AI's *original* recommended group so
      // subsequent Step-2-Return visits keep the emerald KI-Empfehlung
      // highlight on the AI pick — even if the user selects a different
      // group and comes back from Step 3.
      ai.original_suggested_beschaeftigungsgruppe = ai.suggested_beschaeftigungsgruppe || "";
      setAiResult(ai);
      // Eager KI-Auto-Match for Ausbildungsbedingte Vordienstzeiten —
      // pre-fills the Step-3 Ausbildungs-Block so it opens automatically
      // when the user reaches step 3. Iter 19.32: Merge instead of skip,
      // so re-analyze after Q&A adds newly-detected entries (e.g. LAP from
      // the user's answer) without nuking existing AI-suggested rows.
      const eagerAusb = Array.isArray(data?.auto_matched_ausbildung)
        ? data.auto_matched_ausbildung
        : [];
      if (eagerAusb.length > 0) {
        setAusbildungszeiten(mergeAuto(ausbildungszeiten, eagerAusb));
      }
      if (ai.confidence_score >= CONFIDENCE_HIGH) {
        toast.success(
          `Vorschlag: Beschäftigungsgruppe ${ai.suggested_beschaeftigungsgruppe} (Sicherheit ${Math.round(ai.confidence_score * 100)} %).`
        );
      } else {
        toast.info("Es werden noch ein paar Angaben benötigt — bitte die Rückfragen beantworten.");
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
        toast.info("Vorgang abgebrochen.");
      } else {
        setSubmitErr(extractError(err, "Auswertung fehlgeschlagen.", { aiContext: true }));
      }
    } finally {
      analyzeAbortRef.current = null;
      setSubmitting(false);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
    }
  };

  const handleEditAgain = () => {
    setAiResult(null);
    setSubmitErr("");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
  };

  const handleReanalyze = async (qaBlock) => {
    if (!qaBlock || !qaBlock.trim()) return;
    // Append the Q&A to the running description so the user sees their context grow.
    const augmented = description.trim()
      ? `${description.trim()}\n\n--- ZUSÄTZLICHE ANGABEN (vom Benutzer beantwortete Rückfragen) ---\n${qaBlock}`
      : `--- BENUTZER-ANTWORTEN AUF RÜCKFRAGEN ---\n${qaBlock}`;
    setDescription(augmented);
    setSubmitErr("");
    setSubmitting(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
    const controller = new AbortController();
    analyzeAbortRef.current = controller;
    try {
      const fd = new FormData();
      fd.append("kv_id", selectedKv.id);
      fd.append("description", augmented);
      fd.append("consent_ack", "true");
      for (const f of files) fd.append("files", f, f.name);
      const { data } = await api.post("/tool/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
        signal: controller.signal,
      });
      const ai = data?.ai;
      if (!ai) throw new Error("Unerwartete Antwort vom System.");
      // Iter 20.32 — Update the AI-recommended anchor on every re-analyze
      // so the emerald highlight follows the latest LLM decision.
      ai.original_suggested_beschaeftigungsgruppe = ai.suggested_beschaeftigungsgruppe || "";
      setAiResult(ai);
      const eagerAusb = Array.isArray(data?.auto_matched_ausbildung)
        ? data.auto_matched_ausbildung
        : [];
      if (eagerAusb.length > 0) {
        setAusbildungszeiten(mergeAuto(ausbildungszeiten, eagerAusb));
      }
      if (ai.confidence_score >= CONFIDENCE_HIGH) {
        toast.success(
          `Vorschlag: Beschäftigungsgruppe ${ai.suggested_beschaeftigungsgruppe} (Sicherheit ${Math.round(ai.confidence_score * 100)} %).`
        );
      } else {
        toast.info("Es werden noch weitere Angaben benötigt — bitte ergänzen oder eine Gruppe auswählen.");
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
        toast.info("Vorgang abgebrochen.");
      } else {
        setSubmitErr(extractError(err, "Auswertung fehlgeschlagen.", { aiContext: true }));
      }
    } finally {
      analyzeAbortRef.current = null;
      setSubmitting(false);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
    }
  };

  const handleContinueStep3 = (chosenGroup) => {
    // Iter 20.32 — Every "Weiter zu Schritt 3" click passes the currently
    // selected group. We persist it in `user_selected_group` so a round-trip
    // from Step 3 back to Step 2 keeps the orange "AUSGEWÄHLT" highlight
    // on the correct card. `suggested_beschaeftigungsgruppe` is also updated
    // (Downstream Step 3/4/PDF read this field). If the user overrode ET's
    // pick, `manual_override` is flagged. Confidence is always ≥0.9 after
    // an explicit confirmation, since Steps 3 & 4 need a concrete group.
    if (chosenGroup && aiResult) {
      const originalSuggested = String(
        aiResult.original_suggested_beschaeftigungsgruppe ||
          aiResult.suggested_beschaeftigungsgruppe ||
          "",
      ).trim();
      const chosen = String(chosenGroup).trim();
      const isOverride = Boolean(originalSuggested) && chosen !== originalSuggested;
      setAiResult({
        ...aiResult,
        suggested_beschaeftigungsgruppe: chosen,
        user_selected_group: chosen,
        confidence_score: Math.max(aiResult.confidence_score || 0, CONFIDENCE_HIGH),
        manual_override: isOverride || Boolean(aiResult.manual_override),
      });
    }
    setStep(2);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
  };

  const handleStep3Next = () => {
    setStep(3);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
  };

  const handleResetAll = () => {
    setStep(0);
    setSelectedKvId("");
    setFiles([]);
    setDescription("");
    setConsent(false);
    setSubmitErr("");
    setAiResult(null);
    setVordienstzeiten([]);
    setAusbildungszeiten([]);
    const t = new Date();
    setStichtag(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`);
    resetProgress();
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
  };

  // "Neue Einstufung" workflow — entry point used BOTH by the sidebar event
  // (`et:new-einstufung`) AND by the Step 4 "Neue Einstufung starten" button.
  // Confirms only if the user has unsaved input; otherwise silent reset.
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const requestNewEinstufung = useCallback(() => {
    const hasUnsavedWork =
      step > 0 ||
      Boolean(selectedKvId) ||
      files.length > 0 ||
      description.trim().length > 0 ||
      Boolean(aiResult) ||
      vordienstzeiten.length > 0 ||
      ausbildungszeiten.length > 0;
    if (hasUnsavedWork) {
      setShowResetConfirm(true);
    } else {
      handleResetAll();
    }
  }, [step, selectedKvId, files, description, aiResult, vordienstzeiten, ausbildungszeiten]);

  useEffect(() => {
    const onNew = () => requestNewEinstufung();
    window.addEventListener("et:new-einstufung", onNew);
    return () => window.removeEventListener("et:new-einstufung", onNew);
  }, [requestNewEinstufung]);

  return (
    <UserLayout>
      <Toaster position="top-right" richColors closeButton />

      <div data-testid="einstufung-tool-page" className="max-w-6xl">
        <StepIndicator currentStep={step} />

        {step === 0 && (
          <StepOne
            kvs={kvs}
            loading={loading}
            error={error}
            onRetry={loadKvs}
            selectedKvId={selectedKvId}
            setSelectedKvId={setSelectedKvId}
            onToggleFavorite={toggleFavorite}
            stichtag={stichtag}
            setStichtag={setStichtag}
            consent={consent}
            setConsent={setConsent}
            onNext={handleStep1Next}
          />
        )}

        {step === 1 && selectedKv && submitting && !aiResult && <StepTwoLoading />}

        {step === 1 && selectedKv && aiResult && (
          <ResultBlock
            result={aiResult}
            onEdit={handleEditAgain}
            onContinue={handleContinueStep3}
            onReanalyze={handleReanalyze}
            onCancelReanalyze={cancelAnalyze}
            reanalyzing={submitting}
            gruppenOrder={selectedKv.gruppen_order || ""}
          />
        )}

        {step === 1 && selectedKv && !submitting && !aiResult && (
          <StepTwoInputs
            selectedKv={selectedKv}
            files={files}
            setFiles={setFiles}
            description={description}
            setDescription={setDescription}
            consent={consent}
            setConsent={setConsent}
            error={submitErr}
            setError={setSubmitErr}
            submitting={submitting}
            onSubmit={handleStep2Submit}
            onBack={() => setStep(0)}
          />
        )}

        {step === 2 && selectedKv && (
          <StepThree
            selectedKv={selectedKv}
            suggestedGroup={aiResult?.suggested_beschaeftigungsgruppe || ""}
            taetigkeitKurz={aiResult?.extracted_facts?.taetigkeit_kurz || description}
            stichtag={stichtag}
            rows={vordienstzeiten}
            setRows={setVordienstzeitenWithInvalidate}
            ausbildungszeiten={ausbildungszeiten}
            setAusbildungszeiten={setAusbildungszeitenWithInvalidate}
            setBackendAggregate={setBackendAggregate}
            backendAggregate={backendAggregate}
            onBack={() => setStep(1)}
            onNext={handleStep3Next}
          />
        )}

        {step === 3 && selectedKv && (
          <StepFour
            selectedKv={selectedKv}
            context={{
              suggestedGroup: aiResult?.suggested_beschaeftigungsgruppe || "",
              taetigkeitKurz: aiResult?.extracted_facts?.taetigkeit_kurz || description,
              qualifikation: aiResult?.extracted_facts?.qualifikation || "",
              berufsjahre: "",
              stichtag,
              vordienstzeiten,
              ausbildungszeiten,
              // Iter 19.42.4 — Setter weiterreichen, damit StepFour
              // nach erfolgreichem /final-recommendation die `effective_days_anrechenbar`
              // (Backend-Werte nach Overlap-Resolution) in den State mergen kann.
              setVordienstzeiten,
              // Iter 19.43.5 — Backend-autoritativer Aggregate-Setter.
              setBackendAggregate,
              // Iter 19.63.19 — Source-of-Truth-Aggregate (vom Backend in Step 3
              // gesetzt) durchreichen, damit Step 4 die Vor-Empfehlung-Karte
              // mit dem identischen Wert wie Step 3 anzeigt (nicht mit dem
              // FE-rechneten aggregateHybridYMD, das ~2 Tage drift hat).
              backendAggregate,
            }}
            onBack={() => setStep(2)}
            onReset={requestNewEinstufung}
          />
        )}

        {/* Architecture note */}
        <DisclaimerFooter />
      </div>

      {/* "Neue Einstufung" confirmation modal — only shown when unsaved
          inputs exist; otherwise the reset fires silently. */}
      {showResetConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 px-4"
          data-testid="tool-new-einstufung-confirm"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md bg-white border border-ink-200 rounded-sm shadow-xl">
            <div className="px-6 py-5 border-b border-ink-200">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
                Neue Einstufung beginnen
              </div>
              <h2 className="font-cabinet text-xl tracking-tight text-ink-900 font-medium leading-tight">
                Aktuelle Eingaben verwerfen?
              </h2>
            </div>
            <div className="px-6 py-5 text-sm text-warm-700 leading-relaxed">
              Sie haben bereits Daten in dieser Einstufung erfasst. Wenn Sie
              jetzt eine <strong className="text-ink-900">neue Einstufung</strong>{" "}
              beginnen, werden alle Eingaben (Kollektivvertrag, Tätigkeitsbeschreibung,
              hochgeladene Dokumente, Vordienstzeiten) verworfen.
            </div>
            <div className="px-6 py-4 border-t border-ink-200 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowResetConfirm(false)}
                data-testid="tool-new-einstufung-cancel"
                className="inline-flex items-center gap-2 rounded-sm border border-ink-300 bg-white px-3.5 py-2 text-xs font-medium text-ink-700 transition-crisp hover:bg-ink-100"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  setShowResetConfirm(false);
                  handleResetAll();
                }}
                data-testid="tool-new-einstufung-confirm-btn"
                className="inline-flex items-center gap-2 rounded-sm bg-brand-400 px-3.5 py-2 text-xs font-semibold text-white transition-crisp hover:bg-brand-500"
              >
                Eingaben verwerfen & neu beginnen
              </button>
            </div>
          </div>
        </div>
      )}
    </UserLayout>
  );
}
